if (!customElements.get('accessory-product-gallery')) {
  class AccessoryProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-accessory-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-accessory-gallery-slide]')];
      this.dots = [...this.querySelectorAll('[data-accessory-gallery-dot]')];
      this.navigatorItems = [...this.querySelectorAll('[data-accessory-gallery-navigator]')];
      this.index = 0;
      this.pointerStart = null;

      this.querySelector('[data-accessory-gallery-previous]')?.addEventListener('click', () => this.select(this.index - 1));
      this.querySelector('[data-accessory-gallery-next]')?.addEventListener('click', () => this.select(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.select(Number(dot.dataset.accessoryGalleryDot))));
      this.navigatorItems.forEach((item) => item.addEventListener('click', () => this.select(Number(item.dataset.accessoryGalleryNavigator))));

      this.track?.addEventListener('pointerdown', (event) => {
        this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.track.setPointerCapture?.(event.pointerId);
      });
      this.track?.addEventListener('pointerup', (event) => {
        if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY)) this.select(this.index + (deltaX < 0 ? 1 : -1));
      });
      this.track?.addEventListener('pointercancel', () => { this.pointerStart = null; });
    }

    select(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
      this.navigatorItems.forEach((item) => {
        const itemIndex = Number(item.dataset.accessoryGalleryNavigator);
        const active = item.hasAttribute('data-accessory-gallery-navigator-overflow')
          ? this.index >= itemIndex
          : this.index === itemIndex;
        item.classList.toggle('is-current', active);
        item.setAttribute('aria-selected', String(active));
      });
    }

    selectMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.select(index);
    }
  }

  customElements.define('accessory-product-gallery', AccessoryProductGallery);
}

if (!customElements.get('accessory-product-page-controller')) {
  class AccessoryProductPageController extends HTMLElement {}
  customElements.define('accessory-product-page-controller', AccessoryProductPageController);
}

(() => {
  const formatMoney = (amount, currency) => {
    const moneyFormat = window.theme?.settings?.moneyWithCurrencyFormat;
    const placeholder = moneyFormat?.match(/\{\{\s*(\w+)\s*\}\}/)?.[1];

    if (moneyFormat && placeholder) {
      const formatNumber = (precision, thousands, decimal) => {
        const number = (Number(amount) / 100).toFixed(precision);
        const parts = number.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      };
      const formats = {
        amount: () => formatNumber(2, ',', '.'),
        amount_no_decimals: () => formatNumber(0, ',', '.'),
        amount_with_comma_separator: () => formatNumber(2, '.', ','),
        amount_no_decimals_with_comma_separator: () => formatNumber(0, '.', ','),
        amount_with_apostrophe_separator: () => formatNumber(2, "'", '.'),
        amount_with_space_separator: () => formatNumber(2, ' ', ','),
        amount_no_decimals_with_space_separator: () => formatNumber(0, ' ', ','),
        amount_with_period_and_space_separator: () => formatNumber(2, ' ', '.')
      };
      const formattedAmount = (formats[placeholder] || formats.amount)();
      return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, formattedAmount);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
      currencyDisplay: 'code'
    }).format(Number(amount) / 100);
  };

  const getPage = (element) => element.closest('.accessory-product-page');
  let activeModal;
  let activeTrigger;
  let draggedDialog;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragOffsetY = 0;
  let dragStartTime = 0;
  let isDragging = false;
  const mobileMedia = window.matchMedia('(max-width: 767.98px)');

  const resetModalDrag = () => {
    if (draggedDialog) {
      draggedDialog.style.removeProperty('transition');
      draggedDialog.style.removeProperty('transform');
      draggedDialog.closest('.accessory-info-modal')?.style.removeProperty('opacity');
    }
    draggedDialog = null;
    dragOffsetY = 0;
    isDragging = false;
  };

  const closeInfoModal = () => {
    if (!activeModal) return;
    const modal = activeModal;
    const trigger = activeTrigger;
    resetModalDrag();
    modal.classList.remove('is-open');
    window.setTimeout(() => { modal.hidden = true; }, 280);
    document.documentElement.classList.remove('accessory-info-modal-open');
    document.body.classList.remove('accessory-info-modal-open');
    activeModal = null;
    activeTrigger = null;
    trigger?.focus();
  };

  const updateTotal = (page) => {
    const summary = page?.querySelector('[data-accessory-buy-summary]');
    const input = page?.querySelector('[data-accessory-quantity-input]');
    if (!summary || !input) return;
    const quantity = Math.max(1, Number(input.value) || 1);
    input.value = quantity;
    const total = formatMoney(Number(summary.dataset.unitPrice) * quantity, summary.dataset.currency);
    page.querySelector('[data-accessory-total-price]').textContent = total;
    page.dispatchEvent(new CustomEvent('new-buy-buttons:total-updated', {
      bubbles: true,
      detail: { quantity, totalPrice: total }
    }));
  };

  const updateVariant = (page, input, { changeGallery = true, updateUrl = true, emitEvent = true } = {}) => {
    if (!page || !input) return;

    const variantId = input.dataset.variantId;
    const title = input.dataset.variantTitle || input.value;
    const price = Number(input.dataset.variantPrice) || 0;
    const formattedPrice = input.dataset.variantPriceFormatted || formatMoney(price);
    const formattedComparePrice = input.dataset.variantComparePriceFormatted || '';
    const available = input.dataset.variantAvailable === 'true';
    const mediaId = input.dataset.variantMediaId;
    const bundleInputs = [...page.querySelectorAll('[data-accessory-bundle-input]')];

    const idInput = page.querySelector('[data-accessory-product-variant-id]');
    if (idInput) idInput.value = variantId;

    const summary = page.querySelector('[data-accessory-buy-summary]');
    if (summary) summary.dataset.unitPrice = String(price);

    page.querySelectorAll('[data-accessory-unit-price]').forEach((element) => {
      element.textContent = formattedPrice;
    });

    const buyTitle = page.querySelector('[data-accessory-buy-title]');
    if (buyTitle && bundleInputs.length > 1) buyTitle.textContent = title;

    const variantImages = [...page.querySelectorAll('[data-accessory-variant-image]')];
    const activeImage = variantImages.find((image) => image.dataset.accessoryVariantImage === variantId) || variantImages[0];
    variantImages.forEach((image) => image.classList.toggle('is-active', image === activeImage));

    const addButton = page.querySelector('.accessory-buy-box__add');
    if (addButton) {
      addButton.disabled = !available;
      const addButtonLabel = addButton.querySelector('[data-accessory-add-label]');
      if (addButtonLabel) addButtonLabel.textContent = available ? addButton.dataset.addToCartText : addButton.dataset.unavailableText;
    }
    const checkoutButton = page.querySelector('[data-accessory-checkout]');
    if (checkoutButton) checkoutButton.disabled = !available;

    const headerPrice = page.querySelector('[data-accessory-header-price]');
    const priceContainer = headerPrice?.querySelector('.price');
    const currentPrice = headerPrice?.querySelector('.price__current-new');
    const comparePrice = headerPrice?.querySelector('.price__was');
    if (currentPrice) currentPrice.textContent = formattedPrice;
    if (comparePrice) comparePrice.textContent = formattedComparePrice;
    priceContainer?.classList.toggle('price--on-sale', Boolean(formattedComparePrice));
    priceContainer?.classList.toggle('price--sold-out', !available);

    if (changeGallery && mediaId) page.querySelector('accessory-product-gallery')?.selectMedia(mediaId);

    if (emitEvent) {
      page.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          variant: {
            id: variantId,
            title,
            price,
            available,
            option1: input.dataset.variantOption1 || title,
            option2: input.dataset.variantOption2 || null,
            featured_media: mediaId ? { id: mediaId } : null
          }
        }
      }));
    }

    updateTotal(page);

    if (updateUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
    }
  };

  document.addEventListener('click', (event) => {
    const checkoutButton = event.target.closest('[data-accessory-checkout]');
    if (checkoutButton) {
      const form = checkoutButton.closest('form');
      checkoutButton.disabled = true;
      fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      }).then((response) => {
        if (!response.ok) throw new Error('Unable to add this item to the cart.');
        window.location.assign('/checkout');
      }).catch((error) => {
        const errorMessage = form.querySelector('.js-form-error');
        if (errorMessage) {
          errorMessage.textContent = error.message;
          errorMessage.hidden = false;
        }
        checkoutButton.disabled = false;
      });
      return;
    }

    const reviewTrigger = event.target.closest('[data-accessory-review-jump]');
    if (reviewTrigger) {
      const reviewWidget = document.querySelector('#judgeme_product_reviews, .jdgm-widget.jdgm-review-widget, .jdgm-review-widget');
      if (reviewWidget) reviewWidget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const quantityButton = event.target.closest('[data-accessory-quantity]');
    if (quantityButton) {
      const page = getPage(quantityButton);
      const input = page.querySelector('[data-accessory-quantity-input]');
      input.value = Math.max(1, Number(input.value || 1) + (quantityButton.dataset.accessoryQuantity === 'up' ? 1 : -1));
      updateTotal(page);
      return;
    }

    const opener = event.target.closest('[data-accessory-info-modal]');
    if (opener) {
      const modal = document.getElementById(opener.dataset.accessoryInfoModal);
      if (!modal) return;
      document.body.append(modal);
      activeModal = modal;
      activeTrigger = opener;
      modal.hidden = false;
      const dialog = modal.querySelector('.accessory-info-modal__dialog');
      const bounds = dialog.getBoundingClientRect();
      dialog.style.setProperty('--accessory-origin-x', `${event.clientX - bounds.left}px`);
      dialog.style.setProperty('--accessory-origin-y', `${event.clientY - bounds.top}px`);
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.documentElement.classList.add('accessory-info-modal-open');
      document.body.classList.add('accessory-info-modal-open');
      dialog.focus();
      return;
    }

    if (event.target.matches('.accessory-info-modal, .accessory-info-modal__close')) closeInfoModal();
  });

  document.addEventListener('touchstart', (event) => {
    if (!mobileMedia.matches || event.touches.length !== 1) return;
    const dialog = event.target.closest('.accessory-info-modal__dialog');
    if (!dialog || dialog.closest('.accessory-info-modal') !== activeModal || dialog.scrollTop > 0) return;
    draggedDialog = dialog;
    dragStartX = event.touches[0].clientX;
    dragStartY = event.touches[0].clientY;
    dragStartTime = performance.now();
    dragOffsetY = 0;
    isDragging = false;
  }, { passive: true });

  document.addEventListener('touchmove', (event) => {
    if (!draggedDialog || event.touches.length !== 1) return;
    const deltaX = event.touches[0].clientX - dragStartX;
    const deltaY = event.touches[0].clientY - dragStartY;
    if (!isDragging) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        resetModalDrag();
        return;
      }
      if (deltaY <= 4 || draggedDialog.scrollTop > 0) return;
      isDragging = true;
      draggedDialog.style.transition = 'none';
    }
    event.preventDefault();
    dragOffsetY = Math.max(0, deltaY);
    draggedDialog.style.transform = `translate3d(0, ${dragOffsetY}px, 0)`;
    const modal = draggedDialog.closest('.accessory-info-modal');
    if (modal) modal.style.opacity = String(1 - Math.min(dragOffsetY / (draggedDialog.offsetHeight * 1.5), .45));
  }, { passive: false });

  const finishModalDrag = () => {
    if (!draggedDialog) return;
    const elapsed = Math.max(performance.now() - dragStartTime, 1);
    const velocity = dragOffsetY / elapsed;
    const shouldClose = isDragging && (dragOffsetY > Math.min(120, draggedDialog.offsetHeight * .25) || (dragOffsetY > 40 && velocity > .6));
    if (shouldClose) closeInfoModal();
    else resetModalDrag();
  };

  document.addEventListener('touchend', finishModalDrag, { passive: true });
  document.addEventListener('touchcancel', finishModalDrag, { passive: true });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-accessory-bundle-input]')) {
      updateVariant(getPage(event.target), event.target);
      return;
    }
    if (event.target.matches('[data-accessory-quantity-input]')) updateTotal(getPage(event.target));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeInfoModal();
  });

  const initialise = (root = document) => {
    root.querySelectorAll('.accessory-product-page').forEach((page) => {
      const activeVariantId = page.querySelector('[data-accessory-product-variant-id]')?.value;
      const bundleInputs = [...page.querySelectorAll('[data-accessory-bundle-input]')];
      const checkedBundle = bundleInputs.find((input) => input.dataset.variantId === activeVariantId)
        || bundleInputs.find((input) => input.checked)
        || bundleInputs.find((input) => !input.disabled)
        || bundleInputs[0];
      if (checkedBundle) {
        checkedBundle.checked = true;
        updateVariant(page, checkedBundle, { changeGallery: false, updateUrl: false, emitEvent: false });
      } else {
        updateTotal(page);
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  else initialise();

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
